import './List.sass';

import React, { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router5';

import { ListItemProps, ListProps } from './List.interface';
import { IcChevron16 } from '$assets';
import { ModalLink } from '$shared/components';
import { classNames, classNames as cn } from '$utils';

export const List: FC<ListProps> = (props) => {
  const { children, className, title, description, mode = 'normal' } = props;
  if (title || description) {
    return (
      <div className="List__wrapper">
        <div className="List__title">{title}</div>
        <div className={cn('Block List', mode, className)}>{children}</div>
        {!!description && <div className="List__description">{description}</div>}
      </div>
    );
  } else {
    return <div className={cn('Block List', mode, className)}>{children}</div>;
  }
};

export const ListItem: FC<ListItemProps> = (props) => {
  const {
    children,
    indicator = null,
    chevron = false,
    icon = null,
    leftIcon = null,
    leftIconSize = 'normal',
    footer = null,
    modal = null,
    routeName = null,
    routeParams = {},
    href,
    className,
    onClick = undefined,
    iconSize = 'normal',
    description = undefined,
    descriptionSingleLine = false,
    descriptionDisablePadding = false,
    before = null,
    after = null,
    disableHighlight = false,
    color = 'default',
    multipleLine = false,
    ...htmlElementProps
  } = props;

  const contentContainer = useRef<HTMLDivElement>(null);
  const afterContainer = useRef<HTMLDivElement>(null);
  const [isMultipleLineWrap, setMultipleLineWrap] = useState<boolean>(false);

  let Component: any;
  let params: any = {};
  if (modal) {
    Component = ModalLink;
    params.name = modal;
  } else if (routeName) {
    Component = Link;
    params.routeName = routeName;
    params.routeParams = routeParams;
  } else if (href) {
    Component = 'a';
    params.href = href;
    params.target = '_blank';
  } else {
    Component = 'div';
  }

  useEffect(() => {
    if (contentContainer?.current && afterContainer.current && !isMultipleLineWrap) {
      const rect1 = contentContainer.current.getBoundingClientRect();
      const rect2 = afterContainer.current.getBoundingClientRect();

      const distance = Math.sqrt(
        Math.pow(rect2.top - rect1.top, 2) + Math.pow(rect2.left - rect1.left, 2),
      );

      if (distance <= 100) {
        setMultipleLineWrap(true);
      }
    }
  }, [contentContainer, afterContainer, isMultipleLineWrap]);

  return (
    <Component
      className={cn('ListItem', className, {
        touchable: !!onClick,
        disableHighlight,
        [color]: true,
      })}
      {...params}
      onClick={onClick}
      {...htmlElementProps}
    >
      <div
        className={classNames({
          ListItem__wrapper: true,
          multipleLine: multipleLine && isMultipleLineWrap,
        })}
      >
        {before}
        {!!leftIcon && (
          <div
            className={cn({
              ListItem__leftIcon: true,
              [iconSize]: true,
              [leftIconSize]: true,
            })}
          >
            {leftIcon}
          </div>
        )}
        {/*{indicatorType === "primary" ? () :""}*/}
        <div className="ListItem__content" ref={contentContainer}>
          <div className="ListItem__label">{children}</div>
          {!!description && (
            <div
              className={classNames({
                ListItem__description: true,
                withoutPadding: descriptionDisablePadding,
                singleLine: descriptionSingleLine,
              })}
            >
              {description}
            </div>
          )}
        </div>
        {!!icon && (
          <div
            className={cn({
              ListItem__icon: true,
              [iconSize]: true,
            })}
          >
            {icon}
          </div>
        )}
        {!!indicator && <div className="ListItem__indicator">{indicator}</div>}
        <div ref={afterContainer}>{after}</div>
        {chevron && (
          <div className="ListItem__chevron">
            <IcChevron16 />
          </div>
        )}
      </div>
      {footer && <div className="ListItem__footer">{footer}</div>}
    </Component>
  );
};
